import {
  UserRepository,
  Either,
  UserFailure,
  right,
  left,
  VerificationCodeType,
  User,
} from "../../domain";
import { UserRemoteDataSource, UserLocalDataSource } from "../data-sources";
import { UserRemoteDataSourceError } from "../core";

export class UserRepositoryImpl implements UserRepository {
  constructor(
    private readonly remoteDataSource: UserRemoteDataSource,
    private readonly localDataSource: UserLocalDataSource
  ) {}

  private handleError(error: Error): UserFailure {
    const message = error.message as UserRemoteDataSourceError;
    if (message === "NetworkError") return UserFailure.networkFailure();
    if (message === "EmailAlreadyUsedError")
      return UserFailure.emailAlreadyUsedFailure();
    if (message === "PhoneAlreadyUsedError")
      return UserFailure.phoneAlreadyUsedFailure();
    if (message === "WrongPasswordError")
      return UserFailure.wrongPasswordFailure();
    if (message === "WrongVerificationCodeError")
      return UserFailure.wrongVerificationCodeFailure();
    return UserFailure.serverFailure();
  }

  isLoggedIn(): boolean {
    return this.localDataSource.hasToken();
  }

  public async isRegistered(
    phone: string
  ): Promise<Either<UserFailure, boolean>> {
    try {
      return right(await this.remoteDataSource.isRegistered(phone));
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async register(
    firstName: string,
    surName: string,
    phone: string,
    password: string,
    email?: string
  ): Promise<Either<UserFailure, null>> {
    try {
      const token = await this.remoteDataSource.register(
        firstName,
        surName,
        phone,
        password,
        email
      );

      this.localDataSource.setToken(token);

      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async numberRegister(
    phone: string
  ): Promise<Either<UserFailure, null>> {
    try {
      await this.remoteDataSource.numberRegister(phone);
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async loginWithPassword(
    phone: string,
    password: string
  ): Promise<Either<UserFailure, null>> {
    try {
      const token = await this.remoteDataSource.loginWithPassword(
        phone,
        password
      );

      this.localDataSource.setToken(token);

      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async loginWithCode(
    phone: string
  ): Promise<Either<UserFailure, null>> {
    try {
      await this.remoteDataSource.loginWithCode(phone);
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async verifyCode(
    phone: string,
    code: string,
    verificationCodeType: VerificationCodeType
  ): Promise<Either<UserFailure, string>> {
    try {
      const token = await this.remoteDataSource.verifyCode(phone, code);
      if (verificationCodeType === "login") {
        this.localDataSource.setToken(token);
      }
      return right(token);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async forgotPassword(
    phone: string
  ): Promise<Either<UserFailure, null>> {
    try {
      await this.remoteDataSource.forgotPassword(phone);
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async changePassword(
    phone: string,
    password: string,
    token: string
  ): Promise<Either<UserFailure, null>> {
    try {
      await this.remoteDataSource.changePassword(phone, password, token);
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async edit(params: {
    firstName?: string;
    surName?: string;
    phone?: string;
    password?: string;
    email?: string;
  }): Promise<Either<UserFailure, null>> {
    const { email, firstName, password, phone, surName } = params;
    try {
      await this.remoteDataSource.edit(
        firstName,
        surName,
        phone,
        password,
        email
      );
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async getInfo(): Promise<Either<UserFailure, User>> {
    try {
      const res = await this.remoteDataSource.getUserInfo();
      return right(res);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public logOut(): void {
    this.localDataSource.deleteToken();
  }
}
